import { useEffect, useMemo, useState, forwardRef } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { AlertTriangle, UserCog, Settings, Building2, ShieldCheck as ShieldIcon, UserPlus, Package, Wrench, Users } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { api, money } from "@/lib/api";
import { CreateLeadDialog } from "@/components/CreateLeadDialog";
import { QuickOrderDialog } from "@/components/QuickOrderDialog";
import { JobOverview } from "@/components/JobOverview";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/rbac";
import { PersonalPerformance } from "@/components/PersonalPerformance";
import { WarrantyTasks } from "@/components/WarrantyTasks";
import { ProtectionTasks } from "@/components/ProtectionTasks";
import { MissingServiceBanner } from "@/components/MissingServiceBanner";
import { SalesTargets } from "@/components/SalesTargets";
import { PipelineFunnel } from "@/components/PipelineFunnel";
import { WhatsAppInbox } from "@/pages/WhatsAppInbox";
import { WhatsAppPendingCard } from "@/components/WhatsAppPendingCard";
import { ExecutivesManager, ExecScorecard } from "@/components/ExecutivesManager";
import { MmChecklist } from "@/components/MmChecklist";
import { BrandConsultantDashboard, BcMonthlyMessage } from "@/components/BrandConsultant";
import { BrandBriefPanel } from "@/components/BrandConsultant";
import { CEODashboard, CeoMessage } from "@/components/CEODashboard";
import { InvestorDashboard } from "@/components/InvestorDashboard";
import { useStudioList } from "@/lib/useStudioList";
import { Kpi, PageHead, StatusBadge } from "@/components/Bits";
import StudioPerformance from "@/pages/StudioPerformance";
import { RemindersCard } from "@/components/RemindersCard";
import { TargetSummary, TierScore } from "@/components/Progress";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const SUB_TINT = {
  active: "bg-emerald-50 text-emerald-700",
  trial: "bg-amber-50 text-amber-700",
  inactive: "bg-rose-50 text-rose-700",
};

const fmtNum = (n) => (Number(n) || 0).toLocaleString("en-IN");

const MetricCard = ({ label, sub, value, accent, testid, trend }) => (
  <Card className="border-brand-line shadow-none transition-shadow hover:shadow-sm" data-testid={testid}>
    <CardContent className="p-4 sm:p-5">
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</p>
      {sub && <p className="text-[10px] text-slate-400">{sub}</p>}
      <p className={`mt-1 font-display text-2xl font-semibold leading-tight sm:text-3xl ${accent || "text-brand-ink"}`}>
        {value}
      </p>
      {trend}
    </CardContent>
  </Card>
);

const AnchorButton = ({ to, icon: Icon, label, testid }) => (
  <Button
    asChild
    variant="outline"
    className="h-auto min-h-[48px] w-full justify-center gap-2 border-brand-line text-brand-ink hover:bg-brand-tint"
    data-testid={testid}
  >
    <Link to={to}>
      <Icon className="h-4 w-4" />
      <span className="text-sm font-medium">{label}</span>
    </Link>
  </Button>
);

const RecentActivityStrip = ({ jobs }) => {
  const items = (jobs || []).slice(0, 8);
  return (
    <div className="mt-4" data-testid="recent-activity-strip">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Recent activity</p>
        <Link to="/jobs" className="text-xs font-medium text-crimson-600 hover:underline" data-testid="activity-view-all">
          View all
        </Link>
      </div>
      {items.length === 0 ? (
        <p className="rounded-lg border border-dashed border-brand-line py-4 text-center text-xs text-slate-400">
          No recent jobs yet.
        </p>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-1">
          {items.map((j) => (
            <Link
              key={j.id}
              to={`/jobs/${j.id}`}
              data-testid={`activity-${j.id}`}
              className="min-w-[190px] shrink-0 rounded-lg border border-brand-line bg-white p-3 transition-shadow hover:shadow-sm"
            >
              <p className="truncate text-sm font-semibold text-brand-ink">{j.customer_name || "Customer"}</p>
              <p className="truncate text-xs text-slate-500">
                {[j.vehicle?.make, j.vehicle?.model].filter(Boolean).join(" ") || "—"}
              </p>
              <div className="mt-2 flex items-center justify-between">
                <StatusBadge status={j.status} />
                <span className="text-xs font-semibold text-slate-700">{money(j.price || 0)}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const ActionTrigger = forwardRef(({ icon: Icon, label, testid, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    data-testid={testid}
    {...props}
    className="flex h-auto min-h-[60px] w-full items-center justify-start gap-3 rounded-md bg-brand-ink px-4 text-left text-white transition-colors hover:bg-brand-ink/90"
  >
    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/15">
      <Icon className="h-5 w-5" />
    </span>
    <span className="text-base font-semibold">{label}</span>
  </button>
));

const StudioHero = ({ user, hero, card, jobs, onCreated }) => (
  <div data-testid="studio-hero">
    <PageHead title="Studio Overview" subtitle={`${user.studio_name} · this month at a glance`} />
    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4" data-testid="studio-hero-metrics">
      <MetricCard label="Revenue" sub="this month" value={money(hero?.revenue ?? 0)} testid="metric-revenue" />
      <MetricCard label="Profit" sub="this month" value={money(hero?.profit ?? 0)} accent="text-emerald-600" testid="metric-profit" />
      <MetricCard label="Jobs" sub="this month" value={fmtNum(hero?.jobs)} testid="metric-jobs" />
      <MetricCard label="Leads" sub="this month" value={fmtNum(hero?.leads)} testid="metric-leads" />
      <MetricCard label="Tier readiness" value={`${(card?.tier_readiness ?? 0).toFixed(0)}%`} accent="text-brand" testid="metric-tier" />
    </div>
    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="studio-hero-actions">
      <CreateLeadDialog
        onCreated={onCreated}
        trigger={<ActionTrigger icon={UserPlus} label="Create Lead" testid="action-create-lead" />}
      />
      <QuickOrderDialog
        onCreated={onCreated}
        trigger={<ActionTrigger icon={Package} label="Material Order" testid="action-material-order" />}
      />
    </div>
    <div className="mt-3 grid grid-cols-2 gap-3" data-testid="studio-hero-anchors">
      <AnchorButton to="/jobs" icon={Wrench} label="Jobs" testid="anchor-jobs" />
      <AnchorButton to="/customers" icon={Users} label="Customer List" testid="anchor-customers" />
    </div>
    <RecentActivityStrip jobs={jobs} />
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [card, setCard] = useState(null);
  const [hq, setHq] = useState(null);
  const [hqLoading, setHqLoading] = useState(true);
  const [hero, setHero] = useState(null);
  const nav = useNavigate();
  const isNetwork = !!user.is_network;
  const isAdmin = isNetwork;
  const quiet = () => {};
  const { rows: studioList, loading: studiosLoading } = useStudioList(
    isNetwork && can(user, "reports:read")
  );

  useEffect(() => {
    if (user.role === "content_manager") return;
    if (user.role === "installer") {
      api.get("/jobs").then(({ data }) => setJobs(data)).catch(quiet);
      return;
    }
    if (can(user, "reports:read"))
      api.get("/reports/summary").then(({ data }) => setSummary(data)).catch(quiet);
    if (["studio_owner", "studio_manager"].includes(user.role))
      api.get("/reports/studio-hero").then(({ data }) => setHero(data)).catch(quiet);
    if (can(user, "jobs:read"))
      api.get("/jobs").then(({ data }) => setJobs(data.slice(0, 8))).catch(quiet);
    if (can(user, "reminders:read"))
      api
        .post("/reminders/generate")
        .catch(quiet)
        .then(() => api.get("/reminders"))
        .then(({ data }) => setReminders(data))
        .catch(quiet);
    if (isNetwork) {
      if (can(user, "hq:read"))
        api
          .get("/reports/hq")
          .then(({ data }) => setHq(data))
          .catch(quiet)
          .finally(() => setHqLoading(false));
      else setHqLoading(false);
    } else {
      setHqLoading(false);
      if (can(user, "reports:read"))
        api.get("/reports/scorecard").then(({ data }) => setCard(data)).catch(quiet);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.role]);

  const act = async (r) => {
    await api.post(`/reminders/${r.id}/act`);
    const { data } = await api.get("/reminders");
    setReminders(data);
  };

  const reloadStudio = () => {
    api.get("/reports/studio-hero").then(({ data }) => setHero(data)).catch(quiet);
    api.get("/jobs").then(({ data }) => setJobs(data.slice(0, 8))).catch(quiet);
  };

  // Keep the studio hero (e.g. "Content posted this month") fresh without a full page reload:
  // refetch whenever the studio owner/manager returns to this tab (e.g. after publishing content).
  useEffect(() => {
    if (!["studio_owner", "studio_manager"].includes(user.role)) return;
    const refresh = () => {
      if (document.visibilityState === "visible")
        api.get("/reports/studio-hero").then(({ data }) => setHero(data)).catch(quiet);
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.role]);

  // eslint-disable-next-line no-unused-vars
  const networkTotals = useMemo(() => {
    const t = studioList.reduce(
      (a, s) => ({
        revenue: a.revenue + s.month_revenue,
        profit: a.profit + s.month_profit,
        jobs: a.jobs + s.month_jobs,
        reminders: a.reminders + s.pending_reminders,
        tier: a.tier + s.tier_readiness,
      }),
      { revenue: 0, profit: 0, jobs: 0, reminders: 0, tier: 0 }
    );
    return { ...t, tier: studioList.length ? t.tier / studioList.length : 0 };
  }, [studioList]);

  if (user.role === "network_admin") return <CEODashboard adminNav={nav} />;
  if (user.role === "ceo") return <CEODashboard />;
  if (user.role === "studio_investor") return <InvestorDashboard />;
  if (user.role === "content_manager" || user.role === "central_marketing")
    return (
      <div data-testid="marketing-role-home">
        <PersonalPerformance />
        <PageHead title="Dashboard" subtitle={user.studio_name || "Central team"} />
        {user.role === "central_marketing" && (
          <div className="space-y-6">
            <BrandBriefPanel />
            <MmChecklist />
          </div>
        )}
        {user.role === "content_manager" && (
          <p className="text-sm text-slate-500" data-testid="content-manager-empty">
            Nothing to action here right now.
          </p>
        )}
      </div>
    );
  if (user.role === "brand_consultant") return <BrandConsultantDashboard />;
  if (!can(user, "reports:read") && can(user, "finance:read"))
    return <Navigate to="/accounts" replace />;
  if (!can(user, "reports:read") && can(user, "customers:read"))
    return <Navigate to="/customers" replace />;

  if (user.role === "installer") {
    const active = jobs.filter((j) => ["Booked", "In Progress"].includes(j.status));
    return (
      <div>
        <PageHead
          title="My Jobs"
          subtitle={`${user.studio_name} · assigned to you as PPF installer`}
        />
        <div className="mb-6 grid gap-6 sm:grid-cols-3">
          <Kpi label="Assigned Jobs" value={jobs.length} testid="kpi-my-jobs" />
          <Kpi label="Active" value={active.length} testid="kpi-my-active" accent="text-crimson-600" />
          <Kpi
            label="Delivered"
            value={jobs.filter((j) => j.status === "Delivered").length}
            testid="kpi-my-completed"
          />
        </div>
        <JobsTable jobs={jobs} />
      </div>
    );
  }

  if (isAdmin) {
    if (hqLoading || studiosLoading)
      return (
        <div className="space-y-4" data-testid="dashboard-loading">
          <div className="h-28 animate-pulse rounded-md bg-slate-100" />
          <div className="h-24 animate-pulse rounded-md bg-slate-100" />
          <div className="h-64 animate-pulse rounded-md bg-slate-100" />
        </div>
      );
    return (
      <div>
        {["central_sales_team"].includes(user.role) && <WhatsAppPendingCard />}
        <CeoMessage />
        <PersonalPerformance />
        {["central_sales_team"].includes(user.role) && (
          <div className="mb-6 grid gap-6 lg:grid-cols-2" data-testid="dashboard-sales-target">
            <SalesTargets />
            <PipelineFunnel variant="job-mine" />
          </div>
        )}
        {["central_sales_team"].includes(user.role) && (
          <div className="mb-6" data-testid="dashboard-wa-inbox">
            <h2 className="mb-3 text-lg font-semibold text-brand-ink">WhatsApp Inbox</h2>
            <WhatsAppInbox embedded />
          </div>
        )}
        {user.role === "sales_manager_products" && (
          <>
            <ExecutivesManager />
            <ExecScorecard />
            <div className="mb-6"><JobOverview /></div>
            <div className="mb-6" data-testid="dashboard-b2b-funnel">
              <PipelineFunnel variant="b2b" />
            </div>
          </>
        )}
        {user.role === "network_admin" && <BrandBriefPanel />}
        {can(user, "reports:read") && (
          <StudioPerformance rows={studioList} onOpen={(id) => nav(`/studios/${id}`)} />
        )}

        {hq && (
        <>
        <div className="mt-10" />
        <PageHead
          title="HQ Overview"
          subtitle="Veloskin HQ B2B material sales · delivered orders only"
        />
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <Kpi
            label="B2B revenue (delivered)"
            value={money(hq?.totals?.revenue)}
            hint="HQ sale price × quantity"
            testid="kpi-revenue"
          />
          <Kpi
            label="Procurement cost"
            value={money(hq?.totals?.cost)}
            hint="HQ cost price × quantity"
            testid="kpi-hq-cost"
          />
          <Kpi
            label="B2B profit"
            value={money(hq?.totals?.profit)}
            testid="kpi-profit"
            accent="text-emerald-600"
          />
          <Kpi
            label="Delivered orders"
            value={hq?.totals?.delivered_orders ?? 0}
            hint={`${hq?.pipeline?.Submitted ?? 0} submitted · ${hq?.pipeline?.Confirmed ?? 0} confirmed · ${hq?.pipeline?.Dispatched ?? 0} dispatched`}
            testid="kpi-delivered-orders"
          />
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <Card className="border-brand-line shadow-none">
            <CardHeader>
              <CardTitle className="text-lg">B2B by studio</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto p-0">
              <Table data-testid="hq-by-studio-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Studio</TableHead>
                    <TableHead className="text-right">Orders</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead className="text-right">Profit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(hq?.by_studio || []).map((r) => (
                    <TableRow key={r.studio_id} className="hover:bg-brand-tint/60">
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell className="text-right">{r.orders}</TableCell>
                      <TableCell className="text-right">{money(r.revenue)}</TableCell>
                      <TableCell className="text-right font-semibold text-emerald-600">
                        {money(r.profit)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card className="border-brand-line shadow-none">
            <CardHeader>
              <CardTitle className="text-lg">B2B by SKU</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto p-0">
              <Table data-testid="hq-by-sku-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>SKU</TableHead>
                    <TableHead className="text-right">Qty sold</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead className="text-right">Profit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(hq?.by_sku || []).map((r) => (
                    <TableRow key={r.sku} className="hover:bg-brand-tint/60">
                      <TableCell className="font-medium">{r.sku}</TableCell>
                      <TableCell className="text-right">
                        {r.quantity} {r.unit}
                      </TableCell>
                      <TableCell className="text-right">{money(r.revenue)}</TableCell>
                      <TableCell className="text-right font-semibold text-emerald-600">
                        {money(r.profit)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        <Card className="mt-6 border-brand-line shadow-none">
          <CardHeader>
            <CardTitle className="text-lg">All studios — HQ material sales (this month)</CardTitle>
            <p className="mt-1 text-xs text-slate-500">
              B2C job revenue and profit live on each studio's detail page.
            </p>
          </CardHeader>
          <CardContent className="overflow-x-auto p-0">
            <Table data-testid="master-studio-list">
              <TableHeader>
                <TableRow>
                  <TableHead>Studio / region</TableHead>
                  <TableHead className="text-right">B2B revenue (mo)</TableHead>
                  <TableHead className="text-right">B2B profit (mo)</TableHead>
                  <TableHead className="text-right">Orders (mo)</TableHead>
                  <TableHead className="text-right">Target</TableHead>
                  <TableHead className="text-right">Tier</TableHead>
                  <TableHead className="text-right">Reminders</TableHead>
                  <TableHead className="text-right">Subscription</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {studioList.map((s) => (
                  <TableRow
                    key={s.studio_id}
                    data-testid={`master-studio-row-${s.studio_id}`}
                    className="hover:bg-brand-tint/60"
                  >
                    <TableCell>
                      <Link
                        to={`/studios/${s.studio_id}`}
                        data-testid={`dashboard-studio-link-${s.studio_id}`}
                        className="font-medium hover:text-crimson-600"
                      >
                        {s.name}
                      </Link>
                      <p className="text-xs text-slate-500">{s.city}</p>
                    </TableCell>
                    <TableCell className="text-right" data-testid={`b2b-revenue-${s.studio_id}`}>
                      {money(s.b2b_revenue)}
                    </TableCell>
                    <TableCell className="text-right font-semibold text-emerald-600">
                      {money(s.b2b_profit)}
                    </TableCell>
                    <TableCell className="text-right">{s.b2b_orders}</TableCell>
                    <TableCell className="text-right">
                      <span
                        data-testid={`target-progress-${s.studio_id}`}
                        className={
                          s.revenue_progress_pct >= 100
                            ? "font-semibold text-emerald-600"
                            : s.revenue_progress_pct >= 50
                              ? "font-semibold text-amber-600"
                              : "font-semibold text-rose-600"
                        }
                      >
                        {s.revenue_progress_pct}%
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <TierScore score={s.tier_readiness} testid={`tier-score-${s.studio_id}`} />
                    </TableCell>
                    <TableCell className="text-right">
                      {s.pending_reminders > 0 ? (
                        <Badge
                          data-testid={`studio-reminders-${s.studio_id}`}
                          className="border-0 bg-amber-50 text-amber-700"
                        >
                          {s.pending_reminders}
                        </Badge>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {s.low_stock > 0 && (
                          <Badge
                            data-testid={`dashboard-low-stock-${s.studio_id}`}
                            className="border-0 bg-amber-50 text-amber-700"
                          >
                            <AlertTriangle className="mr-1 h-3 w-3" /> {s.low_stock}
                          </Badge>
                        )}
                        <Badge
                          className={`border-0 capitalize ${SUB_TINT[s.subscription_status] || "bg-slate-100"}`}
                        >
                          {s.subscription_status}
                        </Badge>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        </>
        )}

        {reminders.length > 0 && (
          <div className="mt-6">
            <RemindersCard reminders={reminders} onAct={act} showStudio />
          </div>
        )}
        {["network_admin", "ceo"].includes(user.role) && (
          <div className="mt-6 space-y-6">
            <WarrantyTasks />
            <ProtectionTasks />
          </div>
        )}
      </div>
    );
  }

  const t = summary?.totals;
  const isStudioRole = ["studio_owner", "studio_manager"].includes(user.role);
  return (
    <div>
      {isStudioRole ? (
        <>
          <StudioHero user={user} hero={hero} card={card} jobs={jobs} onCreated={reloadStudio} />
          <div className="mt-6 space-y-6">
            <MissingServiceBanner />
            <BcMonthlyMessage />
            <CeoMessage />
            <PersonalPerformance />
          </div>
        </>
      ) : (
        <>
          <PersonalPerformance />
          <PageHead
            title="Studio Overview"
            subtitle={`${user.studio_name} · B2C customer service revenue`}
          />
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi label="B2C revenue (all time)" value={money(t?.revenue)} testid="kpi-revenue" />
            <Kpi
              label="Deductions (GST + material + installer + commission)"
              value={money(t?.material_cost)}
              testid="kpi-material-cost"
            />
            <Kpi label="Profit" value={money(t?.profit)} testid="kpi-profit" accent="text-emerald-600" />
            <Kpi label="Customers" value={t?.customers ?? "—"} testid="kpi-customers" />
          </div>
        </>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Card className="border-brand-line shadow-none lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Monthly targets</CardTitle>
          </CardHeader>
          <CardContent>
            <TargetSummary card={card} />
          </CardContent>
        </Card>
        <Card className="border-brand-line shadow-none">
          <CardHeader>
            <CardTitle className="text-lg">Tier readiness</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className="font-display text-4xl font-semibold text-brand-ink" data-testid="tier-readiness-score">
              {(card?.tier_readiness ?? 0).toFixed(1)}
              <span className="ml-1 text-base font-medium text-slate-400">/100</span>
            </p>
            <p className="text-slate-500">
              Margin {card?.margin_pct ?? 0}%
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <RemindersCard reminders={reminders} onAct={act} />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <PipelineFunnel variant="job-studio" />
      </div>

      <Card className="mt-6 border-brand-line shadow-none">
        <CardHeader>
          <CardTitle className="text-lg">Lead sources</CardTitle>
        </CardHeader>
        <CardContent data-testid="lead-source-breakdown">
          {(summary?.lead_sources || []).map((l) => (
            <div key={l.source} className="mb-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-700">{l.source}</span>
                <span className="font-semibold">{l.count}</span>
              </div>
              <div className="mt-1 h-1.5 rounded bg-slate-100">
                <div
                  className="h-1.5 rounded bg-crimson-500"
                  style={{
                    width: `${(l.count / Math.max(1, summary.lead_sources[0].count)) * 100}%`,
                  }}
                />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="mt-6">
        <h2 className="font-display mb-3 text-lg font-semibold">Recent jobs</h2>
        <JobsTable jobs={jobs} />
      </div>
    </div>
  );
}

const JobsTable = ({ jobs }) => (
  <Card className="border-brand-line shadow-none">
    <CardContent className="overflow-x-auto p-0">
      <Table data-testid="recent-jobs-table">
        <TableHeader>
          <TableRow>
            <TableHead>Customer</TableHead>
            <TableHead>Vehicle</TableHead>
            <TableHead>Services</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Price</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {jobs.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="py-10 text-center text-slate-500">
                No jobs yet.
              </TableCell>
            </TableRow>
          )}
          {jobs.map((j) => (
            <TableRow key={j.id} className="hover:bg-brand-tint/60">
              <TableCell className="font-medium">
                <Link data-testid={`job-link-${j.id}`} to={`/jobs/${j.id}`} className="hover:text-crimson-600">
                  {j.customer_name}
                </Link>
              </TableCell>
              <TableCell className="text-slate-500">
                {j.vehicle ? `${j.vehicle.make} ${j.vehicle.model}` : "—"}
              </TableCell>
              <TableCell className="max-w-xs truncate text-slate-500">
                {(j.services || []).join(", ")}
              </TableCell>
              <TableCell>
                <StatusBadge status={j.status} />
              </TableCell>
              <TableCell className="text-right">{money(j.price)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

const ADMIN_LINKS = [
  { to: "/users", label: "Users", desc: "Add, edit and remove people & assign roles", Icon: UserCog },
  { to: "/studios", label: "Studios", desc: "Studios, staff, installer teams & onboarding", Icon: Building2 },
  { to: "/configuration", label: "Configuration", desc: "Roles, categories, catalog & system settings", Icon: Settings },
  { to: "/warranties", label: "Warranties", desc: "Certificate templates & records", Icon: ShieldIcon },
];

function AdminHome({ nav }) {
  const [ld, setLd] = useState({ studios: [], reps: [] });
  useEffect(() => {
    api.get("/reports/lead-delivered").then(({ data }) => setLd(data)).catch(() => {});
  }, []);
  return (
    <div data-testid="admin-home">
      <PageHead
        title="Admin Console"
        subtitle="Full control — users, roles, studios, categories and system configuration"
      />
      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {ADMIN_LINKS.map(({ to, label, desc, Icon }) => (
          <button
            key={to}
            data-testid={`admin-link-${label.toLowerCase()}`}
            onClick={() => nav(to)}
            className="flex flex-col items-start gap-2 rounded-lg border border-brand-line bg-white p-5 text-left transition-shadow duration-200 hover:shadow-sm"
          >
            <Icon className="h-6 w-6 text-crimson-500" strokeWidth={1.8} />
            <p className="font-display text-base font-semibold text-brand-ink">{label}</p>
            <p className="text-xs text-slate-500">{desc}</p>
          </button>
        ))}
      </div>

      <p className="mb-3 font-display text-lg font-semibold text-brand-ink">Lead → delivered ratio</p>
      <div className="grid gap-4 lg:grid-cols-2">
        {[
          ["By studio", ld.studios, "admin-ld-studios"],
          ["By Central Sales rep", ld.reps, "admin-ld-reps"],
        ].map(([label, rows, testid]) => (
          <Card key={testid} className="border-brand-line shadow-none">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{label}</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto p-0">
              <Table data-testid={testid}>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="text-right">Leads</TableHead>
                    <TableHead className="text-right">Delivered</TableHead>
                    <TableHead className="text-right">Ratio</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="py-6 text-center text-sm text-slate-400">
                        No data yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((r) => (
                    <TableRow key={r.studio_id || r.cs_person_id} className="hover:bg-brand-tint/60">
                      <TableCell className="font-medium text-brand-ink">{r.name}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.leads}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.delivered}</TableCell>
                      <TableCell className="text-right font-semibold tabular-nums text-brand">
                        {r.ratio_pct}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-8 space-y-6">
        <WarrantyTasks />
        <ProtectionTasks />
      </div>
    </div>
  );
}

